<script>
    import { getContext, onMount } from 'svelte';
    import { to } from "@API";
    import { User, Language } from "@Store";
    import jwt_decode from 'jwt-decode';

    let context = getContext("CONTEXT");

    export let billingEmail = null;
    export let onLoginSuccess = () => {};
    export let defaultView = 'register'; // 'register' or 'login'

    let showLogin = defaultView === 'login';
    let showRegister = defaultView === 'register';
    let showForgotPassword = false;
    let password = '';
    let confirmPassword = '';
    let email = billingEmail || '';
    let submitting = false;
    let error = null;
    let forgotPasswordSuccess = false;

    $: if (billingEmail) {
        email = billingEmail;
    }

    async function handleRegister() {
        error = null;
        
        if (!email) {
            error = { message: 'Please enter an email address' };
            return;
        }

        if (!password || password.length < 8) {
            error = { message: 'Password must be at least 8 characters' };
            return;
        }

        if (password !== confirmPassword) {
            error = { message: 'Passwords do not match' };
            return;
        }

        submitting = true;

        let [err, response] = await to(
            Insurecert.API.registration().register({
                email: email.toLowerCase(),
                password
            })
        );

        submitting = false;

        if (err) {
            error = err.data;
            return;
        }

        if (response) {
            localStorage.setItem('insurecert_token', response.token);
            $User = response.user;
            onLoginSuccess();
        }
    }

    async function handleLogin() {
        error = null;

        if (!email) {
            error = { message: 'Please enter an email address' };
            return;
        }

        if (!password) {
            error = { message: 'Please enter your password' };
            return;
        }

        submitting = true;

        let [err, response] = await to(
            Insurecert.API.registration().login({
                email: email.toLowerCase(),
                password
            })
        );

        submitting = false;

        if (err) {
            error = err.data;
            return;
        }

        if (response) {
            localStorage.setItem('insurecert_token', response.jwt_token);
            $User = jwt_decode(response.jwt_token);
            onLoginSuccess();
        }
    }

    async function handleForgotPassword() {
        error = null;
        forgotPasswordSuccess = false;

        if (!email) {
            error = { message: 'Please enter your email address' };
            return;
        }

        submitting = true;

        let [err, response] = await to(
            Insurecert.API.registration().forgotPassword({
                email: email.toLowerCase()
            })
        );

        submitting = false;

        if (err) {
            error = err.data || { message: 'Failed to send reset email. Please try again.' };
            return;
        }

        if (response) {
            forgotPasswordSuccess = true;
        }
    }

    function toggleView() {
        showLogin = !showLogin;
        showRegister = !showRegister;
        showForgotPassword = false;
        error = null;
        password = '';
        confirmPassword = '';
        forgotPasswordSuccess = false;
    }

    function showForgotPasswordView() {
        showForgotPassword = true;
        showLogin = false;
        showRegister = false;
        error = null;
        forgotPasswordSuccess = false;
    }

    function cancelForgotPassword() {
        showForgotPassword = false;
        showLogin = true;
        showRegister = false;
        error = null;
        forgotPasswordSuccess = false;
    }

    function getErrorMessage(errorData) {
        if (typeof errorData === 'string') {
            return errorData;
        }
        if (errorData.message) {
            return errorData.message;
        }
        if (errorData.email) {
            return Array.isArray(errorData.email) ? errorData.email[0] : errorData.email;
        }
        if (errorData.password) {
            return Array.isArray(errorData.password) ? errorData.password[0] : errorData.password;
        }
        return 'An error occurred';
    }

</script>

<div class="auth-overlay">
    <div class="auth-modal">
        <div class="auth-header">
            <div class="brand-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="16" r="14" stroke="#0071e3" stroke-width="2"/>
                    <path d="M16 8v8M16 20h.01" stroke="#0071e3" stroke-width="2" stroke-linecap="round"/>
                </svg>
            </div>
            
            {#if showRegister}
                <h1 class="auth-title">{$Language === 'en' ? 'Create Account' : 'Créer un compte'}</h1>
                <p class="auth-subtitle">{$Language === 'en' ? 'Get started with CAND Insurance' : 'Commencez avec CAND Insurance'}</p>
            {:else if showForgotPassword}
                <h1 class="auth-title">{$Language === 'en' ? 'Reset Password' : 'Réinitialiser le mot de passe'}</h1>
                <p class="auth-subtitle">{$Language === 'en' ? 'Enter your email to receive a reset link' : 'Entrez votre email pour recevoir un lien de réinitialisation'}</p>
            {:else}
                <h1 class="auth-title">{$Language === 'en' ? 'Welcome Back' : 'Bienvenue de retour'}</h1>
                <p class="auth-subtitle">{$Language === 'en' ? 'Sign in to continue' : 'Connectez-vous pour continuer'}</p>
            {/if}
        </div>

        <div class="auth-content">
            {#if showRegister}
                <form on:submit|preventDefault={handleRegister} class="auth-form">
                    <div class="input-group">
                        <label for="email" class="input-label">{$Language === 'en' ? 'Email Address' : 'Adresse email'}</label>
                        {#if billingEmail}
                            <div class="readonly-field">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3.5A2.5 2.5 0 0 1 8 1Z" stroke="currentColor" stroke-width="1.5"/>
                                </svg>
                                {billingEmail}
                            </div>
                        {:else}
                            <input 
                                type="email" 
                                id="email" 
                                bind:value={email} 
                                placeholder="your@email.com"
                                class="auth-input"
                                required
                            />
                        {/if}
                    </div>

                    <div class="input-group">
                        <label for="password" class="input-label">{$Language === 'en' ? 'Password' : 'Mot de passe'}</label>
                        <input 
                            type="password" 
                            id="password" 
                            bind:value={password} 
                            placeholder="Create a strong password"
                            class="auth-input"
                            required
                        />
                        <div class="input-hint">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <circle cx="6" cy="6" r="5" stroke="#86868b" stroke-width="1"/>
                                <path d="M6 3v3M6 7h.01" stroke="#86868b" stroke-width="1" stroke-linecap="round"/>
                            </svg>
                            {$Language === 'en' ? 'Use 8 or more characters with a mix of letters, numbers, and symbols' : 'Utilisez 8 caractères ou plus avec une combinaison de lettres, de chiffres et de symboles'}
                        </div>
                    </div>

                    <div class="input-group">
                        <label for="confirm-password" class="input-label">{$Language === 'en' ? 'Confirm Password' : 'Confirmer le mot de passe'}</label>
                        <input 
                            type="password" 
                            id="confirm-password" 
                            bind:value={confirmPassword} 
                            placeholder="{$Language === 'en' ? 'Re-enter your password' : 'Réentrer votre mot de passe'}"
                            class="auth-input"
                            required
                        />
                    </div>

                    {#if error}
                        <div class="error-message">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/>
                                <path d="M8 4v4M8 10h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                            {getErrorMessage(error)}
                        </div>
                    {/if}

                    <button type="submit" class="auth-button primary" disabled={submitting}>
                        {#if submitting}
                            <svg class="spinner" width="16" height="16" viewBox="0 0 16 16">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="37.7" stroke-dashoffset="37.7">
                                    <animate attributeName="stroke-dasharray" dur="1.5s" values="0 37.7;18.85 18.85;0 37.7" repeatCount="indefinite"/>
                                    <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-18.85;-37.7" repeatCount="indefinite"/>
                                </circle>
                            </svg>
                            {$Language === 'en' ? 'Creating Account...' : 'Création de compte...'}
                        {:else}
                            {$Language === 'en' ? 'Create Account' : 'Créer un compte'}
                        {/if}
                    </button>

                    <button type="button" on:click={toggleView} class="auth-button secondary">
                        {$Language === 'en' ? 'Already have an account? Sign in' : 'Vous avez déjà un compte ? Connectez-vous'}
                    </button>
                </form>
            {/if}

            {#if showLogin}
                <form on:submit|preventDefault={handleLogin} class="auth-form">
                    <div class="input-group">
                        <label for="login-email" class="input-label">{$Language === 'en' ? 'Email Address' : 'Adresse email'}</label>
                        {#if billingEmail}
                            <div class="readonly-field">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3.5A2.5 2.5 0 0 1 8 1Z" stroke="currentColor" stroke-width="1.5"/>
                                </svg>
                                {billingEmail}
                            </div>
                        {:else}
                            <input 
                                type="email" 
                                id="login-email" 
                                bind:value={email} 
                                placeholder="your@email.com"
                                class="auth-input"
                                required
                            />
                        {/if}
                    </div>

                    <div class="input-group">
                        <label for="login-password" class="input-label">{$Language === 'en' ? 'Password' : 'Mot de passe'}</label>
                        <input 
                            type="password" 
                            id="login-password" 
                            bind:value={password} 
                            placeholder="{$Language === 'en' ? 'Enter your password' : 'Entrer votre mot de passe'}"
                            class="auth-input"
                            required
                        />
                    </div>

                    {#if error}
                        <div class="error-message">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/>
                                <path d="M8 4v4M8 10h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                            {getErrorMessage(error)}
                        </div>
                    {/if}

                    <button type="submit" class="auth-button primary" disabled={submitting}>
                        {#if submitting}
                            <svg class="spinner" width="16" height="16" viewBox="0 0 16 16">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="37.7" stroke-dashoffset="37.7">
                                    <animate attributeName="stroke-dasharray" dur="1.5s" values="0 37.7;18.85 18.85;0 37.7" repeatCount="indefinite"/>
                                    <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-18.85;-37.7" repeatCount="indefinite"/>
                                </circle>
                            </svg>
                            {$Language === 'en' ? 'Signing In...' : 'Connexion en cours...'}
                        {:else}
                            {$Language === 'en' ? 'Sign In' : 'Se connecter'}
                        {/if}
                    </button>

                    <button type="button" on:click={showForgotPasswordView} class="auth-button tertiary">
                        {$Language === 'en' ? 'Forgot Password?' : 'Mot de passe oublié ?'}
                    </button>

                    <button type="button" on:click={toggleView} class="auth-button secondary">
                        {$Language === 'en' ? 'Don\'t have an account? Create one' : 'Vous n\'avez pas de compte ? Créez-en un'}
                    </button>
                </form>
            {/if}

            {#if showForgotPassword}
                <form on:submit|preventDefault={handleForgotPassword} class="auth-form">
                    <div class="input-group">
                        <label for="forgot-email" class="input-label">{$Language === 'en' ? 'Email Address' : 'Adresse email'}</label>
                        <input 
                            type="email" 
                            id="forgot-email" 
                            bind:value={email} 
                            placeholder="your@email.com"
                            class="auth-input"
                            required
                        />
                    </div>

                    {#if error}
                        <div class="error-message">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/>
                                <path d="M8 4v4M8 10h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                            {getErrorMessage(error)}
                        </div>
                    {/if}

                    {#if forgotPasswordSuccess}
                        <div class="success-message">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/>
                                <path d="M5.5 8l2 2 3-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            {$Language === 'en' 
                                ? "We've emailed you instructions for resetting your password. You should receive it shortly." 
                                : "Nous vous avons envoyé par courriel les instructions pour réinitialiser votre mot de passe. Vous devriez le recevoir sous peu."}
                        </div>
                    {/if}

                    {#if !forgotPasswordSuccess}
                        <button type="submit" class="auth-button primary" disabled={submitting}>
                            {#if submitting}
                                <svg class="spinner" width="16" height="16" viewBox="0 0 16 16">
                                    <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="37.7" stroke-dashoffset="37.7">
                                        <animate attributeName="stroke-dasharray" dur="1.5s" values="0 37.7;18.85 18.85;0 37.7" repeatCount="indefinite"/>
                                        <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-18.85;-37.7" repeatCount="indefinite"/>
                                    </circle>
                                </svg>
                                {$Language === 'en' ? 'Sending...' : 'Envoi en cours...'}
                            {:else}
                                {$Language === 'en' ? 'Send Reset Link' : 'Envoyer le lien de réinitialisation'}
                            {/if}
                        </button>
                    {/if}

                    <button type="button" on:click={cancelForgotPassword} class="auth-button secondary">
                        {$Language === 'en' ? 'Back to Sign In' : 'Retour à la connexion'}
                    </button>
                </form>
            {/if}
        </div>
    </div>
</div>

<style>
    .auth-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.4);
        backdrop-filter: blur(20px) saturate(180%);
        -webkit-backdrop-filter: blur(20px) saturate(180%);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
        padding: 1rem;
        animation: fadeIn 0.3s ease;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    .auth-modal {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(20px) saturate(180%);
        -webkit-backdrop-filter: blur(20px) saturate(180%);
        border-radius: 24px;
        max-width: 420px;
        width: 100%;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        animation: slideUp 0.4s ease;
        overflow: hidden;
    }

    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    .auth-header {
        text-align: center;
        padding: 2.5rem 2rem 1rem;
        border-bottom: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    .brand-icon {
        margin-bottom: 1.5rem;
    }

    .auth-title {
        font-size: 2rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0 0 0.5rem 0;
        letter-spacing: -0.02em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .auth-subtitle {
        font-size: 1rem;
        color: #86868b;
        margin: 0;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .auth-content {
        padding: 2rem;
    }

    .auth-form {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .input-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .input-label {
        font-size: 0.9375rem;
        font-weight: 500;
        color: #1d1d1f;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .auth-input {
        padding: 1rem 1.25rem;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 12px;
        font-size: 1rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(10px);
        transition: all 0.2s ease;
        outline: none;
    }

    .auth-input:focus {
        border-color: #0071e3;
        background: rgba(255, 255, 255, 0.95);
        box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.1);
    }

    .auth-input::placeholder {
        color: #86868b;
    }

    .readonly-field {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem 1.25rem;
        background: rgba(245, 245, 247, 0.8);
        border-radius: 12px;
        color: #1d1d1f;
        font-weight: 500;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .input-hint {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.8125rem;
        color: #86868b;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .auth-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
        padding: 1rem 1.5rem;
        font-size: 1rem;
        font-weight: 600;
        border: none;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        outline: none;
    }

    .auth-button.primary {
        background: #0071e3;
        color: white;
    }

    .auth-button.primary:hover:not(:disabled) {
        background: #0077ed;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 113, 227, 0.3);
    }

    .auth-button.primary:disabled {
        background: #d2d2d7;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }

    .auth-button.secondary {
        background: transparent;
        color: #0071e3;
        font-weight: 500;
    }

    .auth-button.secondary:hover {
        background: rgba(0, 113, 227, 0.1);
    }

    .auth-button.tertiary {
        background: transparent;
        color: #86868b;
        font-weight: 500;
        font-size: 0.9375rem;
        padding: 0.75rem 1rem;
    }

    .auth-button.tertiary:hover {
        background: rgba(0, 0, 0, 0.05);
        color: #1d1d1f;
    }

    .spinner {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .error-message {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem 1.25rem;
        background: rgba(255, 235, 238, 0.8);
        border: 1px solid rgba(198, 40, 40, 0.2);
        border-radius: 12px;
        color: #c62828;
        font-size: 0.9375rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        animation: shake 0.5s ease;
    }

    .success-message {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 1rem 1.25rem;
        background: rgba(232, 245, 233, 0.9);
        border: 1px solid rgba(46, 125, 50, 0.2);
        border-radius: 12px;
        color: #2e7d32;
        font-size: 0.9375rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        animation: fadeIn 0.3s ease;
        line-height: 1.5;
    }

    .success-message svg {
        flex-shrink: 0;
        margin-top: 0.125rem;
    }

    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        75% { transform: translateX(5px); }
    }

    @media (max-width: 480px) {
        .auth-overlay {
            padding: 0.5rem;
        }

        .auth-modal {
            border-radius: 20px;
        }

        .auth-header {
            padding: 2rem 1.5rem 1rem;
        }

        .auth-title {
            font-size: 1.75rem;
        }

        .auth-content {
            padding: 1.5rem;
        }

        .auth-form {
            gap: 1.25rem;
        }
    }
</style>
